// src/hooks/useCourierPerformance.js
import { useState, useCallback } from 'react';
import {
  getCourierPerformanceSummary,
  getCourierPerformanceDetails
} from '../services/api/courierPerformance';

export const NOT_UPDATED_VALUE = '__NOT_UPDATED__';

export const useCourierPerformance = () => {
  const [summaryData, setSummaryData] = useState(null);
  const [detailsData, setDetailsData] = useState(null);
  const [isLoadingSummary, setIsLoadingSummary] = useState(false);
  const [isLoadingDetails, setIsLoadingDetails] = useState(false);
  const [summaryError, setSummaryError] = useState(null);
  const [detailsError, setDetailsError] = useState(null);

  const fetchSummary = useCallback(async (filters) => {
    try {
      setIsLoadingSummary(true);
      setSummaryError(null);

      const params = {
        groupBy: filters.groupBy,
        ...(filters.shopId !== 'all' && { shopId: filters.shopId }),
        ...(filters.businessCourierId !== 'all' && { businessCourierId: filters.businessCourierId }),
        ...(filters.dateFrom && { dateFrom: filters.dateFrom }),
        ...(filters.dateTo && { dateTo: filters.dateTo })
      };

      const data = await getCourierPerformanceSummary(params);
      setSummaryData(data);
      return data;
    } catch (error) {
      setSummaryError(error.response?.data?.message || error.message);
      throw error;
    } finally {
      setIsLoadingSummary(false);
    }
  }, []);

  const fetchDetails = useCallback(async (filters, page = 1, limit = 50, status = null) => {
    try {
      setIsLoadingDetails(true);
      setDetailsError(null);

      const courierStatusIdsArr = filters.courierStatusIds || [];
      const isNotUpdated = courierStatusIdsArr.includes(NOT_UPDATED_VALUE);
      const realCourierStatusIds = courierStatusIdsArr.filter(id => id !== NOT_UPDATED_VALUE);

      const params = {
        groupBy: filters.groupBy,
        page,
        limit,
        ...(filters.shopId !== 'all' && { shopId: filters.shopId }),
        ...(filters.businessCourierId !== 'all' && { businessCourierId: filters.businessCourierId }),
        ...(filters.dateFrom && { dateFrom: filters.dateFrom }),
        ...(filters.dateTo && { dateTo: filters.dateTo }),
        ...(status && { status }),
        ...(isNotUpdated && { courierStatusNotUpdated: true }),
        ...(realCourierStatusIds.length > 0 && { courierStatusIds: realCourierStatusIds.join(',') })
      };

      const data = await getCourierPerformanceDetails(params);
      setDetailsData(data);
      return data;
    } catch (error) {
      setDetailsError(error.response?.data?.message || error.message);
      throw error;
    } finally {
      setIsLoadingDetails(false);
    }
  }, []);

  const resetSummary = useCallback(() => {
    setSummaryData(null);
    setSummaryError(null);
  }, []);

  const resetDetails = useCallback(() => {
    setDetailsData(null);
    setDetailsError(null);
  }, []);

  const resetAll = useCallback(() => {
    resetSummary();
    resetDetails();
  }, [resetSummary, resetDetails]);

  return {
    summaryData,
    detailsData,
    isLoadingSummary,
    isLoadingDetails,
    summaryError,
    detailsError,
    fetchSummary,
    fetchDetails,
    resetSummary,
    resetDetails,
    resetAll
  };
};